import React, { useMemo, useState } from "react";
import Papa from "papaparse";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import ConfirmAutocloseDialog from "@/components/commission/ConfirmAutocloseDialog";

const IGNORE = "__IGNORE__";

const PROFILE_FIELDS = [
  "company",
  "product",
  "issue_age",
  "agent_id",
  "agent_commission_percent",
  "house_commission_percent",
  "agent_adv_months",
  "house_adv_months",
  "effective_start_date",
  "effective_end_date",
];

const OPTIONAL_FIELDS = new Set(["house_commission_percent", "house_adv_months", "effective_end_date"]);
const REQUIRED_FIELDS = PROFILE_FIELDS.filter((f) => !OPTIONAL_FIELDS.has(f));
const FIELD_LABELS = {
  company: "Company",
  product: "Product",
  issue_age: "Issue Age",
  agent_id: "Sales Agent ID",
  agent_commission_percent: "Agent Commission %",
  house_commission_percent: "House Commission %",
  agent_adv_months: "Agent Adv Months",
  house_adv_months: "House Adv Months",
  effective_start_date: "Profile Start Date",
  effective_end_date: "Profile End Date",
};

function normalizeKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function splitAgentIds(v) {
  if (v === null || v === undefined) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function numMaybe(v) {
  if (v === null || v === undefined) return null;
  const raw = String(v).trim();
  if (!raw) return null;

  // Accept values like: "4.5%", "4.5 %", "1,000", "0%"
  const cleaned = raw.replace(/[%,$]/g, "").trim();
  if (!cleaned) return null;

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export default function ImportCommissionProfilesCsvDialog({ open, onOpenChange, onImported }) {
  const { toast } = useToast();

  const [file, setFile] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});

  const [step, setStep] = useState(1); // 1 upload, 2 map, 3 import, 4 done
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, created: 0, skipped: 0 });
  const [confirmAutocloseOpen, setConfirmAutocloseOpen] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState([]);
  const [pendingItems, setPendingItems] = useState(null);

  const previewRows = useMemo(() => csvRows.slice(0, 15), [csvRows]);

  const reset = () => {
    setFile(null);
    setCsvColumns([]);
    setCsvRows([]);
    setMapping({});
    setStep(1);
    setImporting(false);
    setProgress({ done: 0, total: 0, created: 0, skipped: 0 });
    setConfirmAutocloseOpen(false);
    setPendingConflicts([]);
    setPendingItems(null);
  };

  const autoMap = (cols) => {
    const normCols = new Map(cols.map((c) => [normalizeKey(c), c]));
    const next = {};
    PROFILE_FIELDS.forEach((f) => {
      next[f] = normCols.get(normalizeKey(f)) || (OPTIONAL_FIELDS.has(f) ? IGNORE : "");
    });

    // aliases
    if (next.company === IGNORE) next.company = normCols.get("companyname") || normCols.get("insurancecompany") || IGNORE;
    if (next.product === IGNORE) next.product = normCols.get("productname") || IGNORE;
    if (next.agent_id === IGNORE) next.agent_id = normCols.get("agentid") || normCols.get("agentids") || normCols.get("agents") || IGNORE;
    if (next.agent_commission_percent === IGNORE) next.agent_commission_percent = normCols.get("agentcommission") || IGNORE;
    if (next.house_commission_percent === IGNORE) next.house_commission_percent = normCols.get("housecommission") || IGNORE;
    if (next.agent_adv_months === IGNORE) next.agent_adv_months = normCols.get("agentadvmonths") || IGNORE;
    if (next.house_adv_months === IGNORE) next.house_adv_months = normCols.get("houseadvmonths") || IGNORE;
    if (!next.effective_start_date)
      next.effective_start_date =
        normCols.get("effectivestart") ||
        normCols.get("effectivestartdate") ||
        normCols.get("profilestart") ||
        normCols.get("profilestartdate") ||
        normCols.get("startdate") ||
        "";
    if (next.effective_end_date === IGNORE)
      next.effective_end_date =
        normCols.get("effectiveend") ||
        normCols.get("effectiveenddate") ||
        normCols.get("profileend") ||
        normCols.get("profileenddate") ||
        normCols.get("enddate") ||
        IGNORE;

    REQUIRED_FIELDS.forEach((f) => {
      if (next[f] === IGNORE) next[f] = "";
    });

    return next;
  };

  const onPickFile = (f) => {
    if (!f) return;
    setFile(f);

    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const cols = results?.meta?.fields || [];
        const rows = results?.data || [];
        if (!cols.length) {
          toast({
            title: "CSV needs a header row",
            description: "Please upload a CSV where the first row contains column names.",
            variant: "destructive",
          });
          return;
        }
        setCsvColumns(cols);
        setCsvRows(rows);
        setMapping(autoMap(cols));
        setStep(2);
      },
      error: (err) => {
        toast({ title: "Could not parse CSV", description: String(err), variant: "destructive" });
      },
    });
  };

  const canImport = useMemo(() => {
    return REQUIRED_FIELDS.every((field) => mapping[field] && mapping[field] !== IGNORE);
  }, [mapping]);

  const buildItems = async () => {
    // Load current company/product lists so we can map names -> ids.
    const companiesRes = await api.get("/commission/companies");
    const companies = companiesRes.data || [];

    const companyByNameLc = new Map(companies.map((c) => [String(c.name || "").trim().toLowerCase(), c]));

    const getOrCreateCompanyId = async (name) => {
      const key = String(name || "").trim();
      if (!key) return "";
      const lc = key.toLowerCase();
      const existing = companyByNameLc.get(lc);
      if (existing?.id) return existing.id;
      const res = await api.post("/commission/companies", { name: key });
      const created = res.data;
      if (created?.id) {
        companyByNameLc.set(lc, created);
        return created.id;
      }
      return "";
    };

    const productCacheByCompany = new Map();

    const getOrCreateProductId = async (companyId, productName) => {
      if (!companyId) return "";
      const key = String(productName || "").trim();
      if (!key) return "";

      if (!productCacheByCompany.has(companyId)) {
        const res = await api.get(`/commission/companies/${companyId}/products`);
        const list = res.data || [];
        productCacheByCompany.set(companyId, new Map(list.map((p) => [String(p.name || "").trim().toLowerCase(), p])));
      }

      const map = productCacheByCompany.get(companyId);
      const lc = key.toLowerCase();
      const existing = map.get(lc);
      if (existing?.id) return existing.id;

      const res = await api.post(`/commission/companies/${companyId}/products`, { name: key });
      const created = res.data;
      if (created?.id) {
        map.set(lc, created);
        return created.id;
      }
      return "";
    };

    const items = [];
    let skipped = 0;
    const reasons = {
      missing_company: 0,
      missing_product: 0,
      missing_issue_age: 0,
      missing_agent_ids: 0,
      missing_agent_commission: 0,
      missing_agent_adv_months: 0,
      missing_profile_start_date: 0,
    };

    for (const row of csvRows) {
      const companyName = mapping.company === IGNORE ? "" : row[mapping.company];
      const productName = mapping.product === IGNORE ? "" : row[mapping.product];
      const issueAge = mapping.issue_age === IGNORE ? "" : String(row[mapping.issue_age] ?? "").trim();

      const agentIdRaw = mapping.agent_id === IGNORE ? "" : row[mapping.agent_id];
      const agentIds = splitAgentIds(agentIdRaw);

      const agentCommissionPercent =
        mapping.agent_commission_percent === IGNORE ? null : numMaybe(row[mapping.agent_commission_percent]);
      const houseCommissionPercent =
        mapping.house_commission_percent === IGNORE ? null : numMaybe(row[mapping.house_commission_percent]);
      const agentAdvMonths = mapping.agent_adv_months === IGNORE ? null : numMaybe(row[mapping.agent_adv_months]);
      const houseAdvMonths = mapping.house_adv_months === IGNORE ? null : numMaybe(row[mapping.house_adv_months]);

      const effectiveStartDate =
        mapping.effective_start_date === IGNORE ? "" : String(row[mapping.effective_start_date] ?? "").trim();
      const effectiveEndDate =
        mapping.effective_end_date === IGNORE ? "" : String(row[mapping.effective_end_date] ?? "").trim();

      if (!String(companyName || "").trim()) reasons.missing_company += 1;
      if (!String(productName || "").trim()) reasons.missing_product += 1;
      if (!issueAge) reasons.missing_issue_age += 1;
      if (agentIds.length === 0) reasons.missing_agent_ids += 1;
      if (agentCommissionPercent === null) reasons.missing_agent_commission += 1;
      if (agentAdvMonths === null) reasons.missing_agent_adv_months += 1;
      if (!effectiveStartDate) reasons.missing_profile_start_date += 1;

      const cid = await getOrCreateCompanyId(companyName);
      const pid = await getOrCreateProductId(cid, productName);

      if (
        !cid ||
        !pid ||
        !issueAge ||
        agentIds.length === 0 ||
        agentCommissionPercent === null ||
        agentAdvMonths === null ||
        !effectiveStartDate
      ) {
        skipped += 1;
        continue;
      }

      const safeHouseCommission = houseCommissionPercent === null ? 0 : houseCommissionPercent;
      const safeHouseAdvMonths = houseAdvMonths === null ? null : houseAdvMonths;

      items.push({
        company_id: cid,
        product_id: pid,
        issue_age: issueAge,
        agent_id: agentIds[0],
        effective_start_date: effectiveStartDate,
        effective_end_date: effectiveEndDate || null,
        default_agent_commission_percent: agentCommissionPercent,
        default_agent_advance_months: agentAdvMonths,
        house_commission_percent: safeHouseCommission,
        house_advance_months: safeHouseAdvMonths,
        agent_overrides: [],
      });
    }

    return { items, skipped, reasons };
  };

  const runImport = async ({ confirmAutoclose = false, prepared } = {}) => {
    if (!canImport && !prepared) {
      toast({
        title: "Missing mappings",
        description: "Map all required fields (all except House Commission %, House Adv Months, and Profile End Date).",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    setStep(3);

    try {
      const built = prepared || (await buildItems());
      const { items, skipped, reasons } = built;
      const baseCreated = built?.alreadyCreated || 0;
      if (items.length === 0) {
        const breakdown = reasons
          ? `Missing company: ${reasons.missing_company}, product: ${reasons.missing_product}, issue age: ${reasons.missing_issue_age}, agent id: ${reasons.missing_agent_ids}, profile start date: ${reasons.missing_profile_start_date}, agent comm: ${reasons.missing_agent_commission}, agent adv months: ${reasons.missing_agent_adv_months}.`
          : "";
        toast({
          title: "No valid rows",
          description: breakdown || "No rows had all required fields.",
          variant: "destructive",
        });
        setStep(2);
        return;
      }

      setProgress({ done: 0, total: items.length, created: baseCreated, skipped });

      const chunkSize = 200;
      let done = 0;
      let created = 0;

      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        try {
          const endpoint = confirmAutoclose
            ? "/commission/profiles/bulk-create?confirm_autoclose=true"
            : "/commission/profiles/bulk-create";
          const res = await api.post(endpoint, { items: chunk });
          created += Number(res.data.created || 0);
          done += chunk.length;
          setProgress({ done, total: items.length, created: baseCreated + created, skipped });
        } catch (e) {
          if (e?.response?.status === 409 && !confirmAutoclose) {
            const detail = e?.response?.data?.detail || {};
            setPendingConflicts(detail.conflicts || []);
            const remaining = items.slice(i);
            setPendingItems({ ...built, items: remaining, alreadyCreated: baseCreated + created });
            setConfirmAutocloseOpen(true);
            setStep(2);
            return;
          }
          throw e;
        }
      }

      const totalCreated = baseCreated + created;
      toast({ title: "Import complete", description: `Created ${totalCreated} profiles${skipped ? ` (skipped ${skipped})` : ""}.` });
      setStep(4);
      onImported?.({ created, skipped });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = "Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
          .filter(Boolean)
          .join(" | ");
      } else if (detail && typeof detail === "object") {
        msg = detail?.msg || JSON.stringify(detail);
      }

      toast({ title: "Import failed", description: msg, variant: "destructive" });
      setStep(2);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="commission-import-csv-dialog">
        <DialogHeader>
          <DialogTitle>Upload CSV</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto pr-1">
          {step === 1 ? (
            <div className="space-y-2">
              <Label className="text-slate-200/80">CSV File</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                className="bg-white/5 ring-1 ring-white/10"
                onChange={(e) => onPickFile(e.target.files?.[0] || null)}
                data-testid="commission-import-file"
              />
              <div className="text-xs text-slate-200/60 space-y-1 csv-requirements" data-testid="commission-import-requirements">
                <div>Required columns/values (you can map them in the next step):</div>
                <ul className="list-disc pl-5">
                  <li><span className="text-slate-100">company</span> (Insurance Company name)</li>
                  <li><span className="text-slate-100">product</span> (Product name)</li>
                  <li><span className="text-slate-100">issue_age</span> (e.g., 0-100, 60-80, 85+)</li>
                  <li><span className="text-slate-100">agent_id</span> (MissionAssist internal Agent ID)</li>
                  <li><span className="text-slate-100">agent_commission_percent</span> (required)</li>
                  <li><span className="text-slate-100">agent_adv_months</span> (required)</li>
                  <li><span className="text-slate-100">house_commission_percent</span> (optional)</li>
                  <li><span className="text-slate-100">house_adv_months</span> (optional)</li>
                  <li><span className="text-slate-100">profile_start_date</span> (required, YYYY-MM-DD)</li>
                  <li><span className="text-slate-100">profile_end_date</span> (optional, YYYY-MM-DD)</li>
                </ul>
                <div className="pt-1">Tip: companies/products that don’t exist yet will be created automatically.</div>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-slate-100">Column mapping</div>
                  <div className="text-xs text-slate-200/60">
                    All fields are required except House Commission %, House Adv Months, and Profile End Date.
                  </div>
                </div>
                <div className="flex items-start justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setMapping(autoMap(csvColumns))}
                    data-testid="commission-import-auto-map"
                  >
                    Auto-map
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10 sm:grid-cols-2">
                {PROFILE_FIELDS.map((field) => {
                  const isOptional = OPTIONAL_FIELDS.has(field);
                  const label = FIELD_LABELS[field] || field;
                  const value = mapping[field] || (isOptional ? IGNORE : "");
                  return (
                    <div key={field} className="space-y-1">
                      <div className={"text-xs " + (!isOptional ? "text-indigo-200" : "text-slate-200/70")}>
                        {label} {!isOptional ? "*" : ""}
                      </div>
                      <Select value={value} onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v }))}>
                        <SelectTrigger
                          className="bg-white/5 ring-1 ring-white/10"
                          data-testid={`commission-import-map-${field}`}
                        >
                          <SelectValue placeholder={isOptional ? "Ignore" : "Select column"} />
                        </SelectTrigger>
                        <SelectContent className="glass border-white/10">
                          {isOptional ? <SelectItem value={IGNORE}>Ignore</SelectItem> : null}
                          {csvColumns.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-100">Preview (first {previewRows.length} rows)</div>
                <div className="overflow-auto rounded-2xl ring-1 ring-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-slate-200/70">
                      <tr>
                        {csvColumns.slice(0, 8).map((c) => (
                          <th key={c} className="px-3 py-2 text-left">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, idx) => (
                        <tr key={idx} className="border-t border-white/10">
                          {csvColumns.slice(0, 8).map((c) => (
                            <td key={c} className="px-3 py-2 text-slate-100/90">
                              {String(r[c] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-100">Importing…</div>
              <div className="text-sm text-slate-200/70">
                Imported {progress.done} / {progress.total} • Created: {progress.created}
                {progress.skipped ? ` • Skipped: ${progress.skipped}` : ""}
              </div>
            </div>
          ) : null}

          {step === 4 ? (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-100">Done</div>
              <div className="text-sm text-slate-200/70">
                Created {progress.created} profiles{progress.skipped ? ` (skipped ${progress.skipped})` : ""}.
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          {step === 2 ? (
            <Button
              type="button"
              onClick={() => runImport()}
              disabled={!canImport || importing}
              data-testid="commission-import-run"
            >
              Import
            </Button>
          ) : null}

          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="commission-import-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      <ConfirmAutocloseDialog
        open={confirmAutocloseOpen}
        onOpenChange={setConfirmAutocloseOpen}
        conflicts={pendingConflicts}
        onConfirm={() => {
          if (!pendingItems) return;
          setConfirmAutocloseOpen(false);
          setPendingConflicts([]);
          setPendingItems(null);
          runImport({ confirmAutoclose: true, prepared: pendingItems });
        }}
      />
    </Dialog>
  );
}
